/**
 * Dr. Herança Tenant Configuration
 * 
 * This file exports the configuration for the Dr. Herança tenant.
 */

import { TenantConfig, MenuItems, UserConfig } from 'src/core/types/appConfig';
import { CoreLayout, CoreComponent } from 'src/core/routes';
import PersonIcon from '@mui/icons-material/Person';
import RecentActors from '@mui/icons-material/RecentActors';
import FeedIcon from '@mui/icons-material/Feed';
import { DrHerancaComponent } from './routesTypes';
import { buildInventorySidebarItems } from './pages/inventory/inventoryNav';

/** When `REACT_APP_HIDE_INVENTORY_DOCUMENTS_SECTION` is `true`, hides "Andamento do Inventário e Gestão de Documentos" on the inventory home. */
export const hideInventoryDocumentsSection =
  String(process.env.REACT_APP_HIDE_INVENTORY_DOCUMENTS_SECTION || '').toLowerCase() === 'true';

export const drherancaConfig: TenantConfig = {
    id: 'drheranca',
    name: 'Dr. Herança',
    description: 'Sistema de Cálculo de Inventário e Partilha de Bens',
    
    api: {
        url: process.env.REACT_APP_DR_HERANCA_API || '',
    },
    
    assets: {
        logo: '/static/tenants/drheranca/images/logo/logo.png',
        favicon: '/static/tenants/drheranca/images/favicon.ico',
    },
    
    theme: {
        primaryColor: '#5569ff',
        secondaryColor: '#6E759F',
        accentColor: '#9B2335',
        accentColorLight: '#f7f7f7',
        mode: 'light',
    },

    seo: {
        tagline: 'Calculadora de inventário, simulador de custas e partilha de bens com ITCMD e relatórios',
        keywords:
            'calculadora de inventário, simulador de custos de inventário, cálculo de partilha de bens online, simulador ITCMD, inventário, partilha, herança, ITCMD, meação, Dr. Herança',
        author: 'Dr. Herança',
        ogImage: '/static/tenants/drheranca/images/og-image.png',
        twitterImage: '/static/tenants/drheranca/images/twitter-image.png',
    },

    user: {
        useUserInfo: () => {
            // Lazy import to avoid circular dependency
            // eslint-disable-next-line @typescript-eslint/no-require-imports
            const { useGetUserInfoQuery } = require('./api');
            const result = useGetUserInfoQuery(null, {
                refetchOnMountOrArgChange: true
            });
            // Adapt RTK Query result to expected format
            return {
                data: result.data ? { user: result.data.user } : undefined,
                isLoading: result.isLoading
            };
        },
        menuItems: [
            {
                name: 'Minha Conta',
                link: '/user/profile',
                icon: PersonIcon,
            },
            {
                name: 'Inventários',
                link: '/inventories/list',
                icon: RecentActors,
            },
        ],
    },
    
    routeConfig: [
        {
            type: 'group',
            path: '/',
            layout: CoreLayout.BaseLayout,
            children: [
                {
                    type: 'page',
                    path: '/',
                    component: DrHerancaComponent.Home,
                },
                {
                    type: 'redirect',
                    path: 'home',
                    redirectTo: '/',
                },
                {
                    type: 'page',
                    path: 'blog',
                    component: CoreComponent.BlogList,
                },
                {
                    type: 'page',
                    path: 'blog/:id',
                    component: CoreComponent.BlogArticle,
                },
                {
                    type: 'page',
                    path: 'calculadora-inventario',
                    component: DrHerancaComponent.LandingCalculadoraInventario,
                },
                {
                    type: 'redirect',
                    path: 'calculadora-inventario/:uf',
                    redirectTo: '/calculadora-inventario',
                },
                {
                    type: 'group',
                    path: 'status',
                    children: [
                        {
                            type: 'redirect',
                            path: '/status/',
                            redirectTo: '404',
                        },
                        {
                            type: 'page',
                            path: '404',
                            component: CoreComponent.Status404,
                        },
                        {
                            type: 'page',
                            path: '500',
                            component: CoreComponent.Status500,
                        },
                        {
                            type: 'page',
                            path: 'maintenance',
                            component: CoreComponent.Maintenance,
                        },
                        {
                            type: 'page',
                            path: 'coming-soon',
                            component: CoreComponent.ComingSoon,
                        },
                    ],
                },
                {
                    type: 'page',
                    path: '*',
                    component: CoreComponent.Status404,
                },
            ],
        },
        {
            type: 'group',
            path: '/inventories',
            layout: CoreLayout.SidebarLayout,
            menuItems: [
                {
                    heading: 'Geral',
                    items: [
                        {
                            name: 'Minha Conta',
                            link: '/user/profile',
                            icon: PersonIcon,
                        },
                        {
                            name: 'Inventários',
                            link: '/inventories/list',
                            icon: RecentActors,
                        },
                    ]
                },
            ],
            children: [
                {
                    type: 'redirect',
                    path: '/inventories/',
                    redirectTo: '/inventories/list',
                    private: true,
                },
                {
                    type: 'page',
                    path: 'list',
                    component: DrHerancaComponent.InventoriesList,
                    private: true,
                },
            ],
        },
        {
            type: 'group',
            path: '/inventory',
            layout: CoreLayout.SidebarLayout,
            menuItems: [
                {
                    heading: 'Geral',
                    items: [
                        {
                            name: 'Minha Conta',
                            link: '/user/profile',
                            icon: PersonIcon,
                        },
                        {
                            name: 'Inventários',
                            link: '/inventories/list',
                            icon: RecentActors,
                        },
                    ]
                },
                {
                    heading: 'Inventário',
                    // Derivado da fonte canônica em `pages/inventory/inventoryNav.ts`.
                    // Inclusões, remoções, renomeações ou reordenações de tabs/subtabs
                    // do Inventário propagam-se automaticamente para este menu lateral.
                    items: buildInventorySidebarItems(),
                }
            ],
            children: [
                {
                    type: 'redirect',
                    path: '/inventory/',
                    redirectTo: '/inventory/home',
                    private: true,
                },
                {
                    type: 'page',
                    path: 'delete/:id',
                    component: DrHerancaComponent.InventoryDelete,
                    private: true,
                },
                {
                    type: 'page',
                    path: 'tree/:id',
                    component: DrHerancaComponent.InventoryFamilyTree,
                    private: true,
                },
                {
                    type: 'page',
                    path: 'home/:id',
                    component: DrHerancaComponent.InventoryHome,
                    private: true,
                },
                {
                    type: 'page',
                    path: 'report/:id',
                    component: DrHerancaComponent.InventoryHome,
                    private: true,
                },
                {
                    type: 'page',
                    path: 'print/:id',
                    component: DrHerancaComponent.InventoryPrintPreview,
                    private: true,
                },
                {
                    type: 'page',
                    path: 'home',
                    component: DrHerancaComponent.InventoryHome,
                    private: true,
                },
            ],
        },
        {
            type: 'group',
            path: 'user',
            layout: CoreLayout.SidebarLayout,
            menuItems: [
                {
                    heading: 'Geral',
                    items: [
                        {
                            name: 'Minha Conta',
                            link: '/user/profile',
                            icon: PersonIcon,
                        },
                        {
                            name: 'Inventários',
                            link: '/inventories/list',
                            icon: RecentActors,
                        },
                    ]
                },
            ],
            children: [
                {
                    type: 'page',
                    path: 'profile',
                    component: DrHerancaComponent.UserProfile,
                    private: true,
                },
            ],
        },
        {
            type: 'page',
            path: 'register',
            component: CoreComponent.UserSignUp,
            redirectIfAuthorized: true,
        },
        {
            type: 'page',
            path: 'login',
            component: CoreComponent.UserSignIn,
            redirectIfAuthorized: true,
        },
    ],
    
    signIn: {
        heading: 'Simplifique o cálculo de inventários',
        headingHighlight: 'cálculo de inventários',
        description: 'Uma ferramenta visual e intuitiva para advogados e profissionais da área, que simplifica a partilha de bens.',
        benefits: [
            'Árvore do inventário interativa',
            'Cálculos automatizados de meação e herança',
            'Relatórios detalhados para clientes',
            'Suporte técnico especializado'
        ],
    },
    
    signUp: {
        heading: 'Comece sua jornada com o Dr. Herança',
        headingHighlight: 'com o Dr. Herança',
        description: 'Crie sua conta gratuita e comece a calcular inventários com precisão e rapidez.',
        benefits: [
            'Árvore do inventário interativa',
            'Cálculos automatizados de meação e herança',
            'Relatórios detalhados para clientes',
            'Suporte técnico especializado'
        ],
    },
    
    status500: {
        title: 'Dr. Herança - Erro no Servidor',
        heading: 'Ocorreu um erro, por favor tente novamente mais tarde',
        description: 'O servidor encontrou um erro interno e não foi capaz de completar sua solicitação',
        refreshButtonText: 'Atualizar visualização',
        backButtonText: 'Voltar',
        rightPanelHeading: 'Dr. Herança - Cálculo de Inventário e Partilha de Bens',
        rightPanelDescription: 'Um sistema de cálculo de inventário e partilha de bens de alta performance construído com muitos componentes poderosos do Material-UI em várias niches de produtos para processos de desenvolvimento de aplicativos rápidos e perfeitos.',
        rightPanelButtonLink: '/inventories/list',
        rightPanelButtonText: 'Ir para o Painel Inicial',
    },
};